import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { bmPresenceApi, devicesApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import PageHeader from '../components/PageHeader';
import usePageTitle from '../hooks/usePageTitle';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { AlertCircle, ArrowLeft, Save, Smartphone, RefreshCw, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

/* Section label inside the settings card ("Basic Information", ...). */
const SECTION_LABEL = 'text-xs font-medium uppercase tracking-wide text-muted-foreground';
/* Inputs sit on the glass card: a slightly lighter well, token border. */
const INPUT_CLASS = 'bg-secondary/60 border-border placeholder:text-muted-foreground/70';

/* Same look as PageHeader's own `back` link. Rendered here (not via the `back` prop) so the
 * "back-button" test id survives the Aurora pass - PageHeader does not expose one. */
const BACK_LINK_CLASS = 'inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-2 tap';

export default function DeviceSettingsPage() {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  usePageTitle('Device settings');

  // Fetch device data using the correct API
  const { data: device, isLoading, error } = useQuery({
    queryKey: ['device', deviceId],
    queryFn: () => devicesApi.get(deviceId).then(res => res.data),
  });

  /* Whether a phone is CONNECTED is a fact about right now, held by the PCs running the desktop
     client - there is no column on Device that knows it. The header badge used to read it from
     `status`, a key the server has never sent, so it said "Offline" for every phone including one
     plugged in and running. Same source the Devices list uses, matched on the adb serial. */
  const { data: livePresence } = useQuery({
    queryKey: ['bmPresence'],
    queryFn: () => bmPresenceApi.list().then(res => res.data),
    refetchInterval: 10000,
  });
  const connectedPc = useMemo(() => {
    const udid = device?.udid;
    if (!udid) return null;
    for (const c of (livePresence?.clients || [])) {
      if (!c || !c.client_connected) continue;
      for (const d of (c.devices || [])) {
        if (d && d.serial === udid && d.state === 'device') {
          return c.pc_identifier || c.hostname || 'a connected PC';
        }
      }
    }
    return null;
  }, [livePresence, device]);

  /*
   * Seed the form ONCE, and never overwrite what is being typed.
   *
   * This used to re-seed on every change of the `device` object, which meant any refetch reset the
   * form. That is not theoretical: the Automation switch's mutation invalidates ['device', deviceId]
   * on success, so editing the Label and then toggling automation silently threw the label away -
   * and because setFormData does not clear hasChanges, the Save button stayed lit and would then
   * save the OLD value back. Exactly the shape of the deviceID bug on the slot settings page: state
   * re-seeded from the server after the user had already edited it.
   *
   * Same guard SmmSettingsPage uses ("never overwrite what is being typed"). Values that must stay
   * live - the pause state, the connected PC - are read straight off `device`/presence rather than
   * through formData, so they still update on refetch.
   */
  useEffect(() => {
    if (device && formData === null) {
      setFormData({
        label: device.label || '',
        device_name: device.device_name || '',
        // Device identification (now top-level columns)
        udid: device.udid || '',
        platform_version: device.platform_version || '',
      });
    }
  }, [device, formData]);

  // Update mutation - only send fields that exist in the database
  const updateMutation = useMutation({
    mutationFn: (data) => devicesApi.update(deviceId, {
      device_name: data.device_name,
      label: data.label,
      udid: data.udid,
      platform_version: data.platform_version,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries(['device', deviceId]);
      queryClient.invalidateQueries(['devices']);
      setHasChanges(false);
      toast.success('Device settings saved successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    },
  });

  /*
   * The device-wide pause: its OWN endpoint, saved on click, not gathered into the form's Save.
   * Two reasons it is not a form field. It is the switch an operator reaches for in an incident,
   * and an incident switch that needs a second click on Save is the wrong shape. And a generic
   * device PUT that could move it is exactly how `status` became a control that silently did
   * nothing - the page sent a key, the server dropped it, and nobody found out until someone
   * clicked it expecting automation to stop.
   */
  const pauseMutation = useMutation({
    mutationFn: (enabled) => devicesApi.setEnabled(deviceId, enabled),
    onSuccess: (_res, enabled) => {
      queryClient.invalidateQueries({ queryKey: ['device', deviceId] });
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      toast.success(enabled ? `${deviceId} resumed` : `${deviceId} paused`, {
        description: enabled
          ? 'Slots on this phone start again at their next check.'
          : 'Every slot on this phone stops after its current session. A session already running finishes.',
      });
    },
    onError: (err) => {
      toast.error(err?.response?.status === 404
        ? 'Pausing is not enabled on this server.'
        : (err?.response?.data?.message || 'Could not change automation'));
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => devicesApi.delete(deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries(['devices']);
      toast.success('Device deleted successfully');
      navigate('/devices');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to delete device');
    },
  });

  const handleSave = () => {
    if (!formData) return;
    updateMutation.mutate(formData);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-6 max-w-4xl mx-auto" aria-busy="true" aria-label="Loading device" data-testid="device-settings-skeleton">
        <div className="mb-5 sm:mb-6">
          <div className="h-4 w-20 animate-pulse rounded-xl bg-secondary/60" />
          <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="h-9 w-56 max-w-full animate-pulse rounded-xl bg-secondary/60" />
              <div className="mt-2 h-4 w-40 animate-pulse rounded-xl bg-secondary/60" />
            </div>
            <div className="flex items-center gap-2">
              <div className="h-7 w-20 animate-pulse rounded-full bg-secondary/60" />
              <div className="h-9 w-36 animate-pulse rounded-xl bg-secondary/60" />
            </div>
          </div>
        </div>
        <div className="aurora-card p-5 sm:p-6 space-y-6">
          {[0, 1].map((i) => (
            <div key={i} className="space-y-4">
              <div className="h-3 w-32 animate-pulse rounded-xl bg-secondary/60" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="h-9 w-full animate-pulse rounded-xl bg-secondary/60" />
                <div className="h-9 w-full animate-pulse rounded-xl bg-secondary/60" />
              </div>
            </div>
          ))}
          <div className="h-20 w-full animate-pulse rounded-xl bg-secondary/60" />
        </div>
        <div className="aurora-card p-5 sm:p-6">
          <div className="h-4 w-28 animate-pulse rounded-xl bg-secondary/60" />
          <div className="mt-4 h-12 w-full animate-pulse rounded-xl bg-secondary/60" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="aurora-card p-8 text-center sm:p-10" role="alert" data-testid="device-settings-error">
          <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-danger/10 text-danger ring-1 ring-danger/20">
            <AlertCircle className="h-6 w-6" />
          </span>
          <h3 className="mt-4">Could not load this device</h3>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground text-pretty">
            Failed to load device: {error.message}
          </p>
          <Button variant="outline" onClick={() => navigate('/devices')} className="tap mt-5 rounded-full">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Devices
          </Button>
        </div>
      </div>
    );
  }

  if (!formData) {
    return null;
  }

  const online = !!connectedPc;
  // The pause is server truth, read straight from the device record - never mirrored into
  // formData, so a half-typed form can never make this switch disagree with the server.
  const canPause = device?.device_pause_enabled === true;
  const automationOn = device?.user_enabled !== false;

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="device-settings-page">
      {/* Header */}
      <div>
        <Link to="/devices" className={BACK_LINK_CLASS} data-testid="back-button">
          <ArrowLeft className="h-4 w-4" />
          Devices
        </Link>
        <PageHeader
          icon={Smartphone}
          title={formData.label || 'Device Settings'}
          description={connectedPc
            ? `Connected · reporting from ${connectedPc}`
            : 'Configure device settings'}
          className="mb-0 sm:mb-0"
          actions={(
            <>
              <Badge
                variant="outline"
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 border-transparent',
                  online ? 'bg-success/15 text-success ring-success/25' : 'bg-muted/60 text-muted-foreground ring-border'
                )}
              >
                <span className={cn('h-1.5 w-1.5 rounded-full', online ? 'bg-success animate-pulse' : 'bg-muted-foreground')} aria-hidden />
                {online ? 'Connected' : 'Disconnected'}
              </Badge>
              <Button
                onClick={handleSave}
                disabled={!hasChanges || updateMutation.isPending}
                className="tap rounded-full shadow-glow"
                data-testid="save-button"
              >
                {updateMutation.isPending ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Changes
              </Button>
            </>
          )}
        />
      </div>

      {/* Main Settings Card */}
      <Card className="aurora-card">
        <CardHeader className="p-5 sm:p-6">
          <CardTitle className="font-heading text-base font-semibold">Device Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 p-5 pt-0 sm:p-6 sm:pt-0">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className={SECTION_LABEL}>Basic Information</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 min-w-0">
                <Label htmlFor="label">Device Label</Label>
                <Input
                  id="label"
                  value={formData.label}
                  onChange={(e) => handleInputChange('label', e.target.value)}
                  className={INPUT_CLASS}
                  placeholder="My Phone"
                  data-testid="input-label"
                />
                <p className="text-xs text-muted-foreground">
                  A friendly name to identify this device
                </p>
              </div>

              <div className="space-y-2 min-w-0">
                <Label htmlFor="device_name">Device Model</Label>
                <Input
                  id="device_name"
                  value={formData.device_name}
                  onChange={(e) => handleInputChange('device_name', e.target.value)}
                  className={INPUT_CLASS}
                  placeholder="Pixel 6 Pro"
                  data-testid="input-device-name"
                />
                <p className="text-xs text-muted-foreground">
                  The device model name
                </p>
              </div>
            </div>
          </div>

          {/* Device Identification */}
          <div className="space-y-4">
            <h3 className={SECTION_LABEL}>Device Identification</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 min-w-0">
                <Label htmlFor="udid">UDID (Unique Device ID)</Label>
                <Input
                  id="udid"
                  value={formData.udid}
                  onChange={(e) => handleInputChange('udid', e.target.value)}
                  className={cn(INPUT_CLASS, 'font-mono')}
                  placeholder="1f1a9531"
                  data-testid="input-udid"
                />
                <p className="text-xs text-muted-foreground">
                  The device's unique identifier (from ADB)
                </p>
              </div>

              <div className="space-y-2 min-w-0">
                <Label htmlFor="platform_version">Android Version</Label>
                <Input
                  id="platform_version"
                  value={formData.platform_version}
                  onChange={(e) => handleInputChange('platform_version', e.target.value)}
                  className={cn(INPUT_CLASS, 'font-mono tnum')}
                  placeholder="14"
                  data-testid="input-platform-version"
                />
                <p className="text-xs text-muted-foreground">
                  The Android version running on this device
                </p>
              </div>
            </div>
          </div>

          {/* Automation. Was a "Device Online" switch bound to `status` - a key neither the GET nor
              the PUT has ever carried, so it read Off for every phone and did nothing when clicked.
              Online/offline is a FACT about the connection, not a setting, and it is reported on the
              Devices list next to which PC sees the phone. What belongs here is the one thing an
              operator can actually decide: whether this phone runs automation at all. */}
          <div className="space-y-4">
            <h3 className={SECTION_LABEL}>Automation</h3>

            <div className="flex items-center justify-between gap-4 p-4 rounded-xl bg-secondary/60 ring-1 ring-border/60">
              <div className="min-w-0">
                <Label htmlFor="automation_toggle" className={canPause ? 'cursor-pointer' : undefined}>
                  Run automation on this phone
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  {!canPause
                    ? 'Pausing is not enabled on this server.'
                    : automationOn
                      ? 'ON - every enabled slot assigned to this phone runs normally.'
                      : 'PAUSED - no slot on this phone starts a new session. One already running finishes.'}
                </p>
              </div>
              <Switch
                id="automation_toggle"
                checked={automationOn}
                disabled={!canPause || pauseMutation.isPending}
                onCheckedChange={(checked) => pauseMutation.mutate(checked)}
                data-testid="switch-automation"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="aurora-card !border-danger/30">
        <CardHeader className="p-5 sm:p-6">
          <CardTitle className="font-heading text-base font-semibold text-danger">Danger Zone</CardTitle>
        </CardHeader>
        <CardContent className="p-5 pt-0 sm:p-6 sm:pt-0">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="font-medium">Delete Device</p>
              <p className="text-sm text-muted-foreground text-pretty">
                Permanently remove this device. This action cannot be undone.
              </p>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="tap shrink-0 rounded-full" data-testid="delete-button">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Device
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-card border-border">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-heading">Are you sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete the device "{formData.label}".
                    Any slots assigned to this device will need to be reassigned.
                    This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="gap-2 sm:gap-0">
                  <AlertDialogCancel className="tap bg-secondary/60 border-border">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="tap bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() => deleteMutation.mutate()}
                    data-testid="confirm-delete-button"
                  >
                    {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
